import { forwardRef } from 'react'
import { cn } from '@/lib/cn'

interface GlassInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
}

export const GlassInput = forwardRef<HTMLInputElement, GlassInputProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-white/70">
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            'glass w-full px-4 py-2.5 text-white text-sm',
            'placeholder:text-white/30',
            'focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60 focus:border-[var(--oc-accent)]/50',
            'transition-all duration-200',
            error && 'ring-2 ring-red-500/60 border-red-500/40',
            className,
          )}
          {...props}
        />
        {error && <p className="text-xs text-red-400">{error}</p>}
        {hint && !error && <p className="text-xs text-white/40">{hint}</p>}
      </div>
    )
  },
)
GlassInput.displayName = 'GlassInput'

// Textarea variant
interface GlassTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  hint?: string
}

export const GlassTextarea = forwardRef<HTMLTextAreaElement, GlassTextareaProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-white/70">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          className={cn(
            'glass w-full px-4 py-2.5 text-white text-sm resize-none',
            'placeholder:text-white/30',
            'focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60',
            'transition-all duration-200',
            error && 'ring-2 ring-red-500/60',
            className,
          )}
          {...props}
        />
        {error && <p className="text-xs text-red-400">{error}</p>}
        {hint && !error && <p className="text-xs text-white/40">{hint}</p>}
      </div>
    )
  },
)
GlassTextarea.displayName = 'GlassTextarea'
